import { useMemo } from "react"

interface StringArrayActions {
  /** Add an item (no-op if already present) */
  add: (item: string) => void
  /** Remove an item */
  remove: (item: string) => void
  /** Replace the entire array */
  set: (items: string[]) => void
}

/**
 * Creates stable add/remove/set actions for a string[] filter field.
 *
 * Accepts a functional updater so the returned callbacks never go stale.
 * Pair with `useFilterParams.updateField`:
 *
 * ```ts
 * const updateStatus = useCallback(
 *   (fn: (prev: string[]) => string[]) => updateField("statusFilters", fn),
 *   [updateField],
 * );
 * const status = useStringArrayActions(updateStatus);
 * ```
 */
export function useStringArrayActions(
  update: (updater: (prev: string[]) => string[]) => void,
): StringArrayActions {
  return useMemo(
    () => ({
      add: (item: string) => {
        update((prev) => (prev.includes(item) ? prev : [...prev, item]))
      },
      remove: (item: string) => {
        update((prev) =>
          prev.includes(item) ? prev.filter((s) => s !== item) : prev,
        )
      },
      set: (items: string[]) => {
        update(() => items)
      },
    }),
    [update],
  )
}
